import {DatePicker} from '@github-ui/date-picker'
import type {RangeSelection} from '@github-ui/date-picker/types'
import {CopyIcon, LockIcon, SyncIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, RelativeTime, Text} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {memo, useCallback, useMemo, useRef, useState} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {Recap} from '../../api/scribe/types'
import {useAllColumns} from '../../state-providers/columns/use-all-columns'
import styles from './recap-summary.module.css'

const CUSTOM_PERIOD_VALUE = 'custom' as const

const LONG_DATE_OPTIONS: Intl.DateTimeFormatOptions = {month: 'long', day: 'numeric', year: 'numeric'}
const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {month: 'short', day: 'numeric', year: 'numeric'}
const DATE_LOCALE = 'en-US'

const BASE_PERIOD_OPTIONS = [{value: 'updated:>@today-1w', label: 'Past week'}] as const

const ITERATION_PERIOD_OPTIONS = [
  {value: 'iteration:@current', label: 'Current iteration'},
  {value: 'iteration:@previous', label: 'Previous iteration'},
] as const

const CUSTOM_PERIOD_OPTION = {value: CUSTOM_PERIOD_VALUE, label: 'Specific time frame...'} as const

const ALL_PERIOD_OPTIONS = [...BASE_PERIOD_OPTIONS, ...ITERATION_PERIOD_OPTIONS, CUSTOM_PERIOD_OPTION] as const
const BASE_WITH_CUSTOM_OPTIONS = [...BASE_PERIOD_OPTIONS, CUSTOM_PERIOD_OPTION] as const

type PeriodValue = (typeof ALL_PERIOD_OPTIONS)[number]['value']

function formatDateToISO(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatShortDate = (date: Date) => date.toLocaleDateString(DATE_LOCALE, SHORT_DATE_OPTIONS)

export function formatRecapDateRange(recap: Recap): {dateString: string; showWeekOf: boolean} {
  if (recap.start_time && recap.end_time) {
    const startStr = new Date(recap.start_time).toLocaleDateString(DATE_LOCALE, LONG_DATE_OPTIONS)
    const endStr = new Date(recap.end_time).toLocaleDateString(DATE_LOCALE, LONG_DATE_OPTIONS)
    return {dateString: `${startStr} — ${endStr}`, showWeekOf: false}
  }
  if (recap.created_at) {
    return {dateString: formatShortDate(new Date(recap.created_at)), showWeekOf: true}
  }
  return {dateString: '', showWeekOf: true}
}

interface RecapSummaryProps {
  showLoadingSpinner: boolean
  latestRecap: Recap | null | undefined
  onRegenerate: (filterQuery: string) => void
  onCopy: () => void
  isGeneratingRecap: boolean
}

export const RecapSummary = memo(function RecapSummary({
  showLoadingSpinner,
  latestRecap,
  onRegenerate,
  onCopy,
  isGeneratingRecap,
}: RecapSummaryProps) {
  const {allColumns} = useAllColumns()
  const hasIterationField = useMemo(
    () => allColumns.some(col => col.dataType === MemexColumnDataType.Iteration),
    [allColumns],
  )
  const availableOptions = useMemo(
    () => (hasIterationField ? ALL_PERIOD_OPTIONS : BASE_WITH_CUSTOM_OPTIONS),
    [hasIterationField],
  )

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodValue>(availableOptions[0].value)
  const [customRange, setCustomRange] = useState<RangeSelection | null>(null)
  const datePickerAnchorRef = useRef<HTMLElement>(null)
  const [datePickerOpen, setDatePickerOpen] = useState(false)

  const isCustomWithRange = selectedPeriod === CUSTOM_PERIOD_VALUE && customRange !== null

  const selectedLabel = useMemo(() => {
    if (isCustomWithRange) return `${formatShortDate(customRange.from)} – ${formatShortDate(customRange.to)}`
    return availableOptions.find(opt => opt.value === selectedPeriod)?.label ?? availableOptions[0].label
  }, [isCustomWithRange, customRange, availableOptions, selectedPeriod])

  const filterQuery = useMemo(() => {
    if (isCustomWithRange) {
      return `updated:>${formatDateToISO(customRange.from)} updated:<${formatDateToISO(customRange.to)}`
    }
    return selectedPeriod
  }, [isCustomWithRange, customRange, selectedPeriod])

  const handleDateRangeChange = useCallback((range: RangeSelection | null) => {
    if (!range) return
    setCustomRange(range)
    setSelectedPeriod(CUSTOM_PERIOD_VALUE)
    setDatePickerOpen(false)
  }, [])

  const closeDatePicker = useCallback(() => setDatePickerOpen(false), [])

  if (showLoadingSpinner) {
    return (
      <div className={styles.loadingHeader}>
        <div className={styles.skeletonText}>
          <SkeletonText lines={1} aria-label="Generating recap header" />
        </div>
      </div>
    )
  }

  if (!latestRecap?.created_at) return null

  const {dateString, showWeekOf} = formatRecapDateRange(latestRecap)

  return (
    <div className={styles.recapHeader}>
      <h2 className={styles.insightTitle}>
        {showWeekOf ? `Week of ${dateString}` : dateString}
        <span className={styles.recapDate}>
          Generated <RelativeTime datetime={latestRecap.created_at} /> · <LockIcon size={12} /> Only visible to you
        </span>
      </h2>
      <div className={styles.recapActions}>
        <ActionMenu>
          <ActionMenu.Button>
            <Text weight="light" style={{color: 'var(--fgColor-muted)'}}>
              Period:
            </Text>{' '}
            {selectedLabel}
          </ActionMenu.Button>
          <ActionMenu.Overlay>
            <ActionList selectionVariant="single">
              {availableOptions.map(option => (
                <ActionList.Item
                  key={option.value}
                  selected={selectedPeriod === option.value}
                  onSelect={() => {
                    if (option.value === CUSTOM_PERIOD_VALUE) {
                      setDatePickerOpen(true)
                    } else {
                      setSelectedPeriod(option.value)
                    }
                  }}
                >
                  {option.label}
                </ActionList.Item>
              ))}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
        <DatePicker
          variant="range"
          value={customRange}
          onChange={handleDateRangeChange}
          open={datePickerOpen}
          onClose={closeDatePicker}
          anchor={datePickerAnchorRef}
        />
        <span ref={datePickerAnchorRef} />
        <IconButton
          aria-label="Regenerate recap"
          icon={SyncIcon}
          size="small"
          onClick={() => onRegenerate(filterQuery)}
          disabled={isGeneratingRecap}
        />
        <IconButton
          aria-label="Copy recap markdown"
          icon={CopyIcon}
          size="small"
          onClick={onCopy}
          disabled={isGeneratingRecap}
        />
      </div>
    </div>
  )
})
